import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, X, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve any image reference (full URL, relative /api/files/..., or legacy URL) to a renderable src.
 */
export const resolveImageUrl = (raw) => {
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) return `${BACKEND_URL}${raw}`;
  return raw;
};

/**
 * Multi-image upload component for the admin product form.
 * Props:
 *  - value: array of image URLs / paths
 *  - onChange: callback(newArray)
 *  - max: maximum number of images (default 8)
 */
const ImageUploader = ({ value = [], onChange, max = 8 }) => {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const images = Array.isArray(value) ? value.filter(Boolean) : [];

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (images.length + files.length > max) {
      toast.error(`Μέγιστο ${max} εικόνες`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          const res = await api.post("/uploads/image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push(res.data.url);
        } catch (e) {
          toast.error(`Αποτυχία ${file.name}: ${e.response?.data?.detail || e.message}`);
        }
      }
      if (uploaded.length > 0) {
        onChange([...images, ...uploaded]);
        toast.success(`Ανέβηκαν ${uploaded.length} εικόνες`);
      }
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const remove = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  const move = (idx, dir) => {
    const next = [...images];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid="image-uploader">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
        {images.map((img, idx) => (
          <div key={idx} className="relative aspect-square bg-[#F5F2ED] overflow-hidden group border border-[#EBE5DF]" data-testid={`uploaded-image-${idx}`}>
            <img src={resolveImageUrl(img)} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
            {idx === 0 && (
              <span className="absolute top-2 left-2 bg-[#D4AF37] text-[#2C1E16] text-[9px] tracking-widest uppercase px-2 py-0.5 font-semibold">
                Cover
              </span>
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="p-1.5 bg-white/90 hover:bg-white text-xs disabled:opacity-40"
                title="Πάνω"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === images.length - 1}
                className="p-1.5 bg-white/90 hover:bg-white text-xs disabled:opacity-40"
                title="Κάτω"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1.5 bg-[#8B0000] hover:bg-[#a00000] text-white"
                data-testid={`remove-image-${idx}`}
                title="Αφαίρεση"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}

        {images.length < max && (
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-[#EBE5DF] hover:border-[#D4AF37] hover:bg-[#FAF7EE] transition-colors flex flex-col items-center justify-center gap-2 text-[#736B66] hover:text-[#D4AF37] cursor-pointer disabled:opacity-50"
            data-testid="upload-image-btn"
          >
            {uploading ? (
              <Loader2 size={24} className="animate-spin" />
            ) : (
              <>
                <Upload size={24} />
                <span className="text-[10px] tracking-widest uppercase">Ανέβασμα</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        className="hidden"
        data-testid="file-input"
      />
      <p className="text-xs text-[#736B66]">
        Σύρετε αρχεία ή κάντε κλικ στο κουτί. Επιτρέπονται JPG/PNG/WEBP/GIF έως 8MB. Η πρώτη εικόνα γίνεται cover.
      </p>
    </div>
  );
};

export default ImageUploader;
